import { Linking, Pressable, ScrollView, Text, View } from "react-native";
import * as WebBrowser from "expo-web-browser";
import { decodeEntity } from "html-entities";
import {
  EditIcon,
  FileIcon,
  FileTextIcon,
  ImageIcon,
  LinkIcon,
  MoreVerticalIcon,
} from "lucide-react-native";

import { getFileFormat } from "~/utils/getFileFormat";
import { Image } from "~/components/Image";
import { useCurrentSpace } from "~/hooks/useCurrentSpace";
import { useGetR2AssetURL } from "~/hooks/useGetR2AssetURL";
import { icons, isWeb, isWebDesktop } from "~/lib/constants";
import { cn, formatBytes } from "~/lib/utils";
import { useAccountStore } from "~/stores/space";
import { SaveFile, SaveMedia, SaveNote, SaveUrl } from "~/stores/space/store";
import { Button } from "~/ui/Button";
import { Dropdown } from "~/ui/Dropdown";
import { useAccountActions } from "./AccountActions/context";
import { showConfirmation } from "./confirmation/showConfirmation";
import { getTiptapHTML } from "./SaveForm/AddNote";
import { setParams, useParams } from "./Workspace/params";

export type CardDisplayMode = "grid" | "list";

export function SaveCard({
  save,
  view,
}: {
  save: SaveUrl | SaveMedia | SaveFile | SaveNote;
  view: CardDisplayMode;
}) {
  const formattedDate = new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "numeric",
    year: "numeric",
  }).format(new Date(save.createdAt));

  const { store } = useAccountStore();
  const { space } = useCurrentSpace();
  const { ids = [] } = useParams();
  const isChecked = ids.includes(save.id);

  const toggleSelect = () => {
    setParams({
      ids: ids.includes(save.id)
        ? ids.filter((id) => id !== save.id)
        : Array.from(new Set(ids.concat(save.id))),
    });
  };

  const accountActions = useAccountActions();

  const { getR2AssetURL } = useGetR2AssetURL();

  const dropdown = (
    <Dropdown
      asChild
      trigger={
        <View className="relative w-4">
          <Button
            size="tiny"
            variant="ghost"
            iconBefore={<MoreVerticalIcon size={18} />}
            className="absolute -right-1 -top-1"
            onPress={(e) => {
              e.stopPropagation();
            }}
          />
        </View>
      }
      groups={[
        {
          items: [
            {
              icon: icons.edit,
              title: "Edit",
              key: "edit",

              onSelect: () => {
                console.log(JSON.stringify(save, null, 2));
                accountActions.openEditSave({
                  ...save,
                  spaceId: space.id,
                });
              },
            },
            {
              icon: icons.copy,
              title: "Copy to",
              key: "copy",
              onSelect: () => {
                accountActions.openCopySave({
                  save,
                  srcSpaceId: space.id,
                });
              },
            },
            {
              icon: icons.delete,
              title: "Delete",
              key: "delete",
              destructive: true,
              onSelect: () => {
                showConfirmation({
                  onConfirm: () => {
                    store.deleteSave({
                      saveId: save.id,
                      spaceId: space.id,
                    });
                  },
                  onCancel: () => {
                    console.log("cancel delete save");
                  },
                });
              },
            },
          ],
        },
      ]}
    />
  );

  const getMetadata = () => {
    const type = save.type;
    switch (type) {
      case "URL":
        return (
          <Text className="text-text-tertiary line-clamp-2 text-ellipsis align-middle text-sm sm:line-clamp-1">
            <LinkIcon
              size={14}
              className="text-text-secondary inline shrink-0"
            />{" "}
            {decodeEntity("&middot;")} {formattedDate}{" "}
            {decodeEntity("&middot;")} {new URL(save.url).hostname}
          </Text>
        );
      case "MEDIA":
        return (
          <Text className="text-text-tertiary flex items-center gap-1 text-sm">
            <ImageIcon size={14} className="text-text-secondary  shrink-0" />
            {decodeEntity("&middot;")} {formattedDate}{" "}
            {decodeEntity("&middot;")} {formatBytes(save.meta?.fileSize ?? 0)}{" "}
            {decodeEntity("&middot;")} {save.meta?.type}
          </Text>
        );
      case "FILE":
        return (
          <Text className="text-text-tertiary flex items-center gap-1 text-sm">
            <FileIcon size={14} className="text-text-secondary shrink-0" />{" "}
            {decodeEntity("&middot;")} {formattedDate}{" "}
            {decodeEntity("&middot;")} {formatBytes(save.meta?.size ?? 0)}{" "}
            {decodeEntity("&middot;")} {getFileFormat(save.meta?.mimeType)}
          </Text>
        );
      case "NOTE":
        return (
          <Text className="text-text-tertiary flex items-center gap-1 text-sm">
            <FileTextIcon size={14} className="text-text-secondary shrink-0" />{" "}
            {decodeEntity("&middot;")}
            {formattedDate}
          </Text>
        );
      default:
        console.log("unknown save type", type as unknown);
        return null;
    }
  };

  const title = (
    <Text
      className={cn(
        "text-text-primary line-clamp-2 text-base",
        view === "list" && "text-text-secondary",
        "sm:line-clamp-none",
        isWebDesktop() && "web:!overflow-hidden web:!text-ellipsis",
      )}
      numberOfLines={isWebDesktop() ? 1 : 2}
      ellipsizeMode="tail"
    >
      {(() => {
        switch (save.type) {
          case "URL":
            return save.title || save.meta?.title;
          case "MEDIA":
            return save.title || save.meta?.fileName;
          case "FILE":
            return save.title || save.meta?.name;
          case "NOTE":
            return save.title || save.description;
        }
      })() || decodeEntity("&nbsp;")}
    </Text>
  );

  const onPress = () => {
    switch (save.type) {
      case "URL":
        return WebBrowser.openBrowserAsync(save.url, {
          presentationStyle: WebBrowser.WebBrowserPresentationStyle.POPOVER,
          enableBarCollapsing: true,
        });
      case "MEDIA":
        return window.open(getR2AssetURL(save.r2Key), "_blank");
      case "FILE":
        if (isWeb) {
          return window.open(getR2AssetURL(save.r2Key), "_blank");
        }
        // @ts-expect-error ...
        return Linking.openURL(save.meta?.uri);
      case "NOTE":
        accountActions.openEditSave({
          spaceId: space.id,
          ...save,
          step: "takeNote",
        });
    }
  };

  const getImageProps = () => {
    const getAltText = () => {
      switch (save.type) {
        case "URL":
          return save.title || save.meta?.title;
        case "MEDIA":
          return save.title || save.meta?.fileName;
        case "FILE":
          return save.title || save.meta?.name;
      }
    };
    return {
      source: (() => {
        switch (save.type) {
          case "URL":
            return save.meta?.image ?? save.meta?.logo;
          case "FILE":
          case "MEDIA":
            return getR2AssetURL(save.r2Key);
          case "NOTE":
            return "";
        }
      })(),

      alt: getAltText() ?? "undefined",
      "aria-label": getAltText() ?? undefined,
      placeholder: {
        uri: "thumbhash:/1QcSHQRnh493V4dIh4eXh1h4kJUI",
      },
    } as const;
  };

  const editButton = (
    <Button
      size="small"
      variant="outline"
      onPress={() => {
        accountActions.openEditSave({
          ...save,
          spaceId: space.id,
        });
      }}
      iconBefore={<EditIcon size={16} />}
    />
  );

  if (view === "list") {
    return (
      <>
        <View className="group flex-row items-center gap-4">
          <Pressable
            className={cn(
              "grow flex-row items-start gap-4 px-4 py-3",
              isChecked && "bg-grey-dark-5",
            )}
            onLongPress={() => {
              toggleSelect();
            }}
            onPress={onPress}
          >
            {(() => {
              if (save.type === "MEDIA" || save.type === "URL") {
                return (
                  <Image
                    {...getImageProps()}
                    style={{
                      width: 80,
                      height: 60,
                      borderRadius: 8,
                    }}
                  />
                );
              }

              if (save.type === "FILE") {
                return (
                  <View
                    className="items-center justify-center"
                    style={{
                      width: 80,
                      height: 60,
                    }}
                  >
                    <FileIcon size={24} className="text-text-primary" />
                  </View>
                );
              }
            })()}

            <View className="flex-1 gap-1">
              {title}
              {getMetadata()}
            </View>
          </Pressable>
          <View className="ml-auto hidden self-center px-4 group-hover:flex group-has-[[aria-expanded=true]]:flex">
            {dropdown}
          </View>
        </View>
      </>
    );
  }

  return (
    <Pressable onPress={onPress}>
      <View className="bg-surface-secondary group relative grid h-full grid-cols-1 grid-rows-[auto_auto] overflow-hidden rounded-lg">
        {(() => {
          if (save.type === "MEDIA" || save.type === "URL") {
            return (
              <Image
                {...getImageProps()}
                style={{
                  aspectRatio: 120 / 63,
                }}
              />
            );
          }

          if (save.type === "FILE") {
            return (
              <View className="items-center justify-center">
                <FileIcon size={28} className="text-text-primary" />
              </View>
            );
          }

          if (save.type === "NOTE") {
            return (
              <View className="items-center justify-center p-2">
                {isWeb ? (
                  <div
                    className="tiptap overflow-hidden"
                    dangerouslySetInnerHTML={{
                      __html: (() => {
                        try {
                          return getTiptapHTML(JSON.parse(save.richTextAST));
                        } catch {
                          return "";
                        }
                      })(),
                    }}
                  />
                ) : null}
              </View>
            );
          }
        })()}

        <View className="border-surface-tertiary h-full flex-1 gap-1 border-t px-3 py-4">
          <View className="flex-row gap-2">
            {title}
            <View className="ml-auto hidden group-hover:flex group-has-[[aria-expanded=true]]:flex">
              {dropdown}
            </View>
          </View>
          {getMetadata()}
        </View>
      </View>
    </Pressable>
  );
}
